import { designVariables as tk } from "@studio3t/design-variables";
import { BoxModel } from "@studio3t/generic-components";
import { useVirtualizer } from "@tanstack/react-virtual";
import type { OverlayScrollbarsComponentRef } from "overlayscrollbars-react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { type DisplayMessage, MessageType } from "@/api/ai/types";
import { JumpingDots } from "@/components/JumpingDots";
import { OverlayScroll } from "@/components/OverlayScroll";
import { SkeletonBar, SkeletonText } from "@/components/Skeleton";
import { ChatMessage } from "./ChatMessage";
import { ChatResponse } from "./ChatResponse";
import { type ChatRow, chatRows, loadingIndicatorFor } from "./chatRows";

/** Where the window sits: a narrow helper sidebar, or the Agent page's
 *  body, where the rows share a centered column with the footer card. */
export type ChatLayout = "sidebar" | "page";

interface ChatWindowProps {
  messages: DisplayMessage[];
  isThinking: boolean;
  /** The conversation's history has not arrived yet: a skeleton holds
   *  the place of the greeting or the messages. */
  loading?: boolean;
  layout?: ChatLayout;
  onApplyCode?: (
    code: string,
    lang: string,
    artifact: string | null,
    name: string | null,
  ) => void;
}

// The "Generating…" indicator: on from a pending prompt, off once the
// answer streams, otherwise off a beat later.
function useShowLoading(messages: DisplayMessage[]): boolean {
  const [showLoading, setShowLoading] = useState(false);
  useEffect(() => {
    const verdict = loadingIndicatorFor(messages[messages.length - 1]);
    if (verdict !== "hide-later") {
      setShowLoading(verdict === "show");
      return;
    }
    const timer = setTimeout(() => setShowLoading(false), 10);
    return () => clearTimeout(timer);
  }, [messages]);
  return showLoading;
}

function MessageRow({
  message,
  onApplyCode,
}: {
  message: DisplayMessage;
  onApplyCode: ChatWindowProps["onApplyCode"];
}) {
  if (message.type === MessageType.User) {
    return (
      <ChatMessage timestamp={message.timestamp}>{message.message}</ChatMessage>
    );
  }
  return (
    <ChatResponse
      messageId={message.messageId}
      onApplyCode={onApplyCode}
      toolRounds={message.toolRounds}
      toolCalls={message.toolCalls}
    >
      {message.message}
    </ChatResponse>
  );
}

function ChatRowView({
  row,
  onApplyCode,
}: {
  row: ChatRow;
  onApplyCode: ChatWindowProps["onApplyCode"];
}) {
  switch (row.kind) {
    case "skeleton":
      return <ChatSkeleton />;
    case "greeting":
      return (
        <GreetingMessage data-testid="ai-greeting-message">
          Hi! I am your AI assistant for Rhai script generation.
          <br />
          <br />I can help you:
          <ul>
            <li>Generate transformation scripts from natural language</li>
            <li>Explain existing script</li>
            <li>Fix errors when transformations fail</li>
            <li>Suggest masking patterns for PII data</li>
          </ul>
          Try asking me something like: “
          <ExamplePrompt>Mask the email field and redact the SSN</ExamplePrompt>
          ”
        </GreetingMessage>
      );
    case "message":
      return <MessageRow message={row.message} onApplyCode={onApplyCode} />;
    case "generating":
      return (
        <GeneratingIndicator data-testid="generating-indicator">
          <JumpingDots />
          <LoadingText>Generating...</LoadingText>
        </GeneratingIndicator>
      );
  }
}

// How close to the bottom (px) still counts as "reading the latest".
const NEAR_BOTTOM = 40;

function readerAtBottom(viewport: HTMLElement): boolean {
  return (
    viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight <
    NEAR_BOTTOM
  );
}

// Scrolls to the bottom, flagging the move until the frame that
// dispatches its scroll event has passed — that event must not read as
// the reader scrolling away from the bottom.
function pinToBottom(viewport: HTMLElement, pinning: { current: boolean }) {
  pinning.current = true;
  viewport.scrollTop = viewport.scrollHeight;
  requestAnimationFrame(() => {
    pinning.current = false;
  });
}

// The list follows the conversation: a new message always brings the
// bottom into view, and while the reader is at the bottom it stays there
// as the last answer streams and grows, a viewer inside it mounts and
// changes its height, or the panel resizes. A reader who scrolled up to
// re-read is left alone until the next message. The follow is resize
// driven (the list box and the viewport), not effect driven: a row can
// grow after React's effects for that render already ran.
function useFollowBottom(
  viewport: HTMLElement | null,
  list: HTMLElement | null,
  messageCount: number,
) {
  const atBottom = useRef(true);
  const pinning = useRef(false);

  useEffect(() => {
    if (!viewport) return;
    const onScroll = () => {
      if (!pinning.current) atBottom.current = readerAtBottom(viewport);
    };
    viewport.addEventListener("scroll", onScroll, { passive: true });
    const observer = new ResizeObserver(() => {
      if (atBottom.current) pinToBottom(viewport, pinning);
    });
    for (const el of [viewport, list]) if (el) observer.observe(el);
    return () => {
      viewport.removeEventListener("scroll", onScroll);
      observer.disconnect();
    };
  }, [viewport, list]);

  // A new message re-pins the reader, wherever they were.
  // biome-ignore lint/correctness/useExhaustiveDependencies: the count is the trigger
  useLayoutEffect(() => {
    atBottom.current = true;
    if (viewport) pinToBottom(viewport, pinning);
  }, [viewport, messageCount]);
}

// The estimate stands in only until a row is measured; a mid-size
// message, so the scrollbar is about right from the first paint.
const ESTIMATED_ROW = 120;

/**
 * Chat window: the conversation's scroll region (the app's themed
 * scrollbars) with its messages virtualized — only the rows in and
 * around the viewport are in the DOM, measured as they render (markdown,
 * code viewers and a streaming answer all change height). A long
 * conversation costs the same to show as a short one.
 */
export const ChatWindow: React.FC<ChatWindowProps> = ({
  messages,
  isThinking,
  loading = false,
  onApplyCode,
  layout = "sidebar",
}) => {
  const spacing = LAYOUT[layout];
  const showLoading = useShowLoading(messages);
  const rows = useMemo(
    () => chatRows(messages, showLoading || isThinking, loading),
    [messages, showLoading, isThinking, loading],
  );

  const scrollRef = useRef<OverlayScrollbarsComponentRef>(null);
  const [viewport, setViewport] = useState<HTMLElement | null>(null);
  const [list, setList] = useState<HTMLElement | null>(null);
  // The scroll element is OverlayScrollbars' React-rendered viewport —
  // there from the first render, so the rows never wait on the
  // (deferred) scrollbar init.
  useLayoutEffect(() => {
    setViewport(
      scrollRef.current
        ?.getElement()
        ?.querySelector<HTMLElement>("[data-overlayscrollbars-contents]") ??
        null,
    );
  }, []);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => viewport,
    estimateSize: () => ESTIMATED_ROW,
    overscan: 3,
    gap: ROW_GAP,
    paddingStart: spacing.paddingTop,
    paddingEnd: spacing.paddingBottom,
    getItemKey: (index) => rows[index].key,
  });
  useFollowBottom(viewport, list, messages.length);

  return (
    <ScrollArea ref={scrollRef}>
      <VirtualList
        ref={setList}
        data-testid="ai-chat-window"
        $layout={layout}
        style={{ height: virtualizer.getTotalSize() }}
      >
        {virtualizer.getVirtualItems().map((v) => (
          <VirtualRow
            key={v.key}
            data-index={v.index}
            ref={virtualizer.measureElement}
            style={{ transform: `translateY(${v.start}px)` }}
          >
            <ChatRowView row={rows[v.index]} onApplyCode={onApplyCode} />
          </VirtualRow>
        ))}
      </VirtualList>
    </ScrollArea>
  );
};

// The shape of a short exchange — a prompt bubble on the right, an
// answer's lines on the left — while the history is fetched.
function ChatSkeleton() {
  return (
    <SkeletonExchange aria-hidden="true" data-testid="ai-chat-skeleton">
      <SkeletonPrompt>
        <SkeletonBar $width="100%" />
      </SkeletonPrompt>
      <SkeletonText lines={4} />
      <SkeletonPrompt>
        <SkeletonBar $width="100%" />
      </SkeletonPrompt>
      <SkeletonText lines={2} />
    </SkeletonExchange>
  );
}

const SkeletonExchange = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${tk.primitives.spacings.xl}px;
`;

const SkeletonPrompt = styled.div`
  align-self: flex-end;
  width: 45%;
`;

// The list's spacing: the Figma chat body's paddings and the gap between
// messages, handed to the virtualizer as its paddings and gap. The Agent
// page's body (Figma 13017:15059) pads 40px top and bottom, 18px aside,
// and holds the rows in an 800px column.
const ROW_GAP = tk.primitives.spacings.xl;
export const PAGE_CONTENT_WIDTH = 800;
const LAYOUT: Record<
  ChatLayout,
  { paddingTop: number; paddingBottom: number; paddingX: number }
> = {
  sidebar: {
    paddingTop: BoxModel.Sidebar.List.Item.Gap,
    paddingBottom: tk.primitives.spacings.l,
    paddingX: tk.ui.semantic.spacingm,
  },
  page: {
    paddingTop: tk.ui.semantic.spacingxl,
    paddingBottom: tk.ui.semantic.spacingxl,
    paddingX: tk.ui.semantic.spacingl,
  },
};

const ScrollArea = styled(OverlayScroll)`
  flex: 1;
  min-height: 0;
  align-self: stretch;
`;

// Styled components - aligned with mario/ui patterns. The side padding
// is a variable so the absolutely placed rows inset by the same amount;
// the page layout caps the column at the content width and centers it.
const VirtualList = styled.div<{ $layout: ChatLayout }>`
  --chat-padding-x: ${(p) => LAYOUT[p.$layout].paddingX}px;
  --chat-greeting-size: ${(p) =>
    p.$layout === "page"
      ? tk.type.paragraph.large.regular.fontSize
      : tk.type.paragraph.default.regular.fontSize}px;
  position: relative;
  width: 100%;
  max-width: ${(p) =>
    p.$layout === "page"
      ? `calc(${PAGE_CONTENT_WIDTH}px + 2 * var(--chat-padding-x))`
      : "none"};
  margin: 0 auto;
  padding: 0 var(--chat-padding-x);
  box-sizing: border-box;
  color: ${(props) => props.theme.semanticTextPlaceholder};
  user-select: text;

  ul,
  ol {
    padding-left: 13px;
    margin-top: ${tk.primitives.spacings.xl}px;
    margin-bottom: ${tk.primitives.spacings.a12}px;
  }

  li {
    padding-left: 0px !important;
  }

  p {
    font-size: 14px;
    line-height: 140%;
    margin-bottom: ${tk.primitives.spacings.m}px;
  }
`;

// A rendered row, placed at its measured offset; the horizontal padding
// lives on the list so the rows span the same width the flow layout gave
// them.
const VirtualRow = styled.div`
  position: absolute;
  top: 0;
  left: var(--chat-padding-x);
  right: var(--chat-padding-x);
`;

const GreetingMessage = styled.div`
  color: ${(props) => props.theme.semanticTextBody};
  font-size: var(--chat-greeting-size, 14px);
  line-height: 1.4;

  ul {
    margin: 20px 0;
  }

  li {
    font-weight: 500;
  }
`;

const ExamplePrompt = styled.span`
  font-style: italic;
`;

const GeneratingIndicator = styled.div`
  display: flex;
  align-items: center;
  font-size: 16px;
  color: ${(props) => props.theme.semanticTextPlaceholder};
`;

const LoadingText = styled.span`
  color: ${(props) => props.theme.semanticTextPlaceholder};
  text-align: right;
  font-variant-numeric: lining-nums tabular-nums;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 120%;
`;

export default ChatWindow;
